import { LanguageMaps } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { InternalTag, InternalTagType } from '../../../types/InternalTag'
import { InternalTagGroup, TagGroupMutation, TagGroupOrder, TagGroupType } from '../types/InternalTagGroup'

export const tagGroupTypes: TagGroupType[] = [
  InternalTagType.Feature,
  InternalTagType.Game,
  InternalTagType.Implementation,
  InternalTagType.Chapter,
  InternalTagType.Brand,
  InternalTagType.Event,
  InternalTagType.EventSecondary,
]

export const tagGroupTypeLabelKeys: Record<TagGroupType, string> = {
  [InternalTagType.Feature]: 'internal-service-management:tag_groups_type_feature',
  [InternalTagType.Game]: 'internal-service-management:tag_groups_type_game',
  [InternalTagType.Implementation]: 'internal-service-management:tag_groups_type_implementation',
  [InternalTagType.Chapter]: 'internal-service-management:tag_groups_type_chapter',
  [InternalTagType.Brand]: 'internal-service-management:tag_groups_type_brand',
  [InternalTagType.Event]: 'internal-service-management:tag_groups_type_event',
  [InternalTagType.EventSecondary]: 'internal-service-management:tag_groups_type_event_secondary',
}

export const unknownTagName = '---'

export type TagGroupRow = {
  tagGroup: InternalTagGroup
  names: ContentLanguageTranslation
  tagNames: string[]
}

export type TagGroupFormValues = {
  names: ContentLanguageTranslation
  colorHex: string
  type: TagGroupType
  tagIds: string[]
}

// Plain code-unit comparison, as the legacy `orderBy`, keeps the order analysts are used to.
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

export const getTagNames = (tagIds: string[], tags: InternalTag[]): string[] => {
  const namesById = new Map(tags.map((tag) => [tag.id, tag.name]))
  return tagIds.map((tagId) => namesById.get(tagId) ?? unknownTagName).sort(byCodeUnit)
}

// Groups without an order go last, as with the legacy `sortBy`.
export const buildTagGroupRows = (
  tagGroups: InternalTagGroup[],
  languageMaps: LanguageMaps | undefined,
  tags: InternalTag[],
  type: TagGroupType
): TagGroupRow[] =>
  tagGroups
    .filter((tagGroup) => tagGroup.type === type)
    .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
    .map((tagGroup) => ({
      tagGroup,
      names: {
        en: tagGroup.name,
        ja: languageMaps?.ja?.tagGroups?.[tagGroup.id]?.name ?? '',
        zh: languageMaps?.zh?.tagGroups?.[tagGroup.id]?.name ?? '',
      },
      tagNames: getTagNames(tagGroup.tags ?? [], tags),
    }))

export const filterTagGroupRows = (rows: TagGroupRow[], search: string): TagGroupRow[] => {
  const needle = search.trim().toLowerCase()
  return needle ? rows.filter((row) => row.tagGroup.name?.toLowerCase().includes(needle)) : rows
}

// The legacy app numbers the groups of one type from 1 in their shown order.
export const toTagGroupOrder = (rows: TagGroupRow[]): TagGroupOrder => Object.fromEntries(rows.map((row, index) => [row.tagGroup.id, index + 1]))

export const getTagOptions = (tags: InternalTag[], type: TagGroupType): InternalTag[] =>
  tags.filter((tag) => tag.type === type).sort((a, b) => byCodeUnit(a.name, b.name))

export const isValidColorHex = (value: string): boolean => !value.trim() || /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())

export const toTagGroupFormValues = (row: TagGroupRow | undefined, defaultType: TagGroupType): TagGroupFormValues => ({
  names: row?.names ?? {},
  colorHex: row?.tagGroup.colorHex ?? '',
  type: row?.tagGroup.type ?? defaultType,
  tagIds: row?.tagGroup.tags ?? [],
})

export type TagGroupField = 'name' | 'type' | 'colorHex' | 'tags'

export const tagGroupFieldLabelKeys: Record<TagGroupField, string> = {
  name: 'internal-service-management:tag_groups_name',
  type: 'internal-service-management:tag_groups_type',
  colorHex: 'internal-service-management:tag_groups_color',
  tags: 'internal-service-management:tag_groups_tags',
}

// The backend answers 200 while dropping fields, so the saved group it returns is compared with what was sent.
export const getUnstoredTagGroupFields = (sent: TagGroupMutation, stored: InternalTagGroup): TagGroupField[] => {
  const unstored: TagGroupField[] = []
  if ((stored.name ?? '') !== sent.name) unstored.push('name')
  if (stored.type !== sent.type) unstored.push('type')
  if ((stored.colorHex || null) !== sent.colorHex) unstored.push('colorHex')
  if ((stored.tags ?? []).join() !== sent.tags.join()) unstored.push('tags')
  return unstored
}

export const toTagGroupMutation = (original: InternalTagGroup | undefined, values: TagGroupFormValues): TagGroupMutation => ({
  ...original,
  name: values.names.en ?? '',
  type: values.type,
  colorHex: values.colorHex.trim() || null,
  tags: values.tagIds,
  translations: {
    en: { name: values.names.en ?? '' },
    ja: { name: values.names.ja ?? '' },
    zh: { name: values.names.zh ?? '' },
  },
})
